import {
	Button,
	Group,
	LoadingOverlay,
	Modal,
	Space,
	Text,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconCancel } from '@tabler/icons-react';
import { useParams } from 'react-router-dom';
import { Title } from '../../../components/Title';
import { FormButtons } from '../../../components/form/FormButtons';
import { defaultIconStyle } from '../../../theme/styling/iconStyling';
import { mutate } from '../../../util/api/mutate';
import { useUpsertSupplierForm } from '../hooks/useUpsertSupplierForm';
import type { UpsertSupplierDto } from '../types/SupplierDto';
import { UpsertSupplierForm } from './UpsertSupplierForm';

interface NewSupplierModalProps {
	close: () => void;
	opened: boolean;
	refreshTable: () => void;
}

export function NewSupplierModal({
	close,
	opened,
	refreshTable,
}: NewSupplierModalProps) {
	const { organisation } = useParams();

	const [
		showSuccessModal,
		{ open: openSuccessModal, close: closeSuccessModal },
	] = useDisclosure(false);

	const { form, inputs } = useUpsertSupplierForm();

	const newSupplierMutation = mutate<UpsertSupplierDto, number>(
		`${organisation}/supplier/new`,
		inputs,
	);

	const cancelClose = () => {
		form.reset();
		close();
		closeSuccessModal();
	};

	const successClose = () => {
		cancelClose();
		refreshTable();
	};

	return (
		<>
			<Modal
				opened={opened}
				onClose={cancelClose}
				title={<Title label={'New Supplier'} />}
				w={100}
				withCloseButton={true}
				size={'lg'}
				centered
			>
				<LoadingOverlay visible={newSupplierMutation.isLoading} />
				<Modal
					opened={showSuccessModal}
					onClose={successClose}
					title={<Title label={'Supplier Added!'} />}
					centered
				>
					<Text>
						A supplier for {inputs.branchIds.length} {'branch(es)'} has been
						successfully added. Click Ok to continue...
					</Text>
					<Group justify={'flex-end'}>
						<Button
							color={'medepos.1'}
							variant={'filled'}
							leftSection={<IconCancel {...defaultIconStyle} />}
							onClick={() => successClose()}
						>
							Ok
						</Button>
					</Group>
				</Modal>
				<Space h={6} />
				<form
					onSubmit={form.onSubmit(async () => {
						await newSupplierMutation.mutateAsync().then((response) => {
							if (response.isValid) {
								openSuccessModal();
							}
						});
					})}
				>
					<UpsertSupplierForm form={form} />
					<Space h={20} />
					<FormButtons close={cancelClose} />
				</form>
			</Modal>
		</>
	);
}
